/**
 * Refetches every Supabase-backed store slice that feeds useDashboardStats / home dashboard widgets.
 *
 * ── Staleness model ────────────────────────────────────────────────────────────────────────────
 * Each store slice has a TTL bucket. A fetch is skipped if the store was fetched more recently
 * than its TTL, *unless* `force: true` is passed (used on explicit navigation to the dashboard).
 *
 * Buckets:
 *   hot   (2 min)  — user-facing work: tasks, items, triage
 *   warm  (5 min)  — operational: cycles, tracks, sessions, loops, proposals, relationships
 *   cool  (10 min) — slow-moving metadata: attributes, item pools / publications
 *
 * This prevents both:
 *   a) hammer on rapid navigation (TTL blocks duplicate in-flight requests), and
 *   b) stale widgets during long dashboard sessions (polling in HomeDashboardPage forces
 *      re-fetch once TTLs expire).
 */

import { getCurrentWorkspaceId } from './supabaseSync';
import { useItemsStore } from '../stores/prioritization/itemsStore';
import { useItemPoolsStore } from '../stores/prioritization/itemPoolsStore';
import { useDegapProcessStore } from '../stores/prioritization/degapProcessStore';
import { useSessionStore } from '../stores/prioritization/sessionStore';
import { usePrioritizationTaskStore } from '../stores/prioritization/prioritizationTaskStore';
import { useAttributesStore } from '../stores/prioritization/attributesStore';
import { useCatchballLoopStore } from '../stores/prioritization/catchballLoopStore';
import { useProposalPacketStore } from '../stores/prioritization/proposalPacketStore';
import { useItemRelationshipsStore } from '../stores/prioritization/itemRelationshipsStore';
import { useTriageStore } from '../stores/prioritization/triageStore';
import { useTrackStore } from '../stores/prioritization/trackStore';

// ─── TTL constants ─────────────────────────────────────────────────────────────

const HOT_TTL_MS  = 2  * 60 * 1000;   // 2 minutes  — tasks, items, triage
const WARM_TTL_MS = 5  * 60 * 1000;   // 5 minutes  — cycles, tracks, sessions, loops, proposals
const COOL_TTL_MS = 10 * 60 * 1000;   // 10 minutes — attributes, pools, relationships

// ─── Per-store last-fetched registry ──────────────────────────────────────────
// Module-level so it persists across renders but resets on full page reload.

const lastFetchedAt = new Map<string, number>();

function isStale(key: string, ttlMs: number): boolean {
  const last = lastFetchedAt.get(key);
  if (last === undefined) return true;
  return Date.now() - last > ttlMs;
}

function markFetched(key: string): void {
  lastFetchedAt.set(key, Date.now());
}

/** Returns current staleness age in ms (for debugging / logging). */
export function getStoreAge(key: string): number | null {
  const last = lastFetchedAt.get(key);
  return last !== undefined ? Date.now() - last : null;
}

/** Force-expire all store TTLs so the next refresh call re-fetches everything. */
export function invalidateDashboardCache(): void {
  lastFetchedAt.clear();
}

// ─── Main refresh function ─────────────────────────────────────────────────────

export interface RefreshOptions {
  /**
   * When true, bypasses all TTL checks and refetches every store unconditionally.
   * Use on explicit user navigation to the dashboard or after a mutation that changes
   * data visible in widgets (e.g. submitting a new item, archiving a strategy).
   */
  force?: boolean;
}

export function refreshPrioritizationDashboardAggregates(options: RefreshOptions = {}): void {
  const { force = false } = options;

  // ── HOT: tasks, items, triage ───────────────────────────────────────────────
  if (force || isStale('items', HOT_TTL_MS)) {
    markFetched('items');
    useItemsStore.getState().fetchFromSupabase?.();
  }

  if (force || isStale('triage', HOT_TTL_MS)) {
    markFetched('triage');
    useTriageStore.getState().fetchFromSupabase?.();
  }

  if (force || isStale('tasks', HOT_TTL_MS)) {
    markFetched('tasks');
    usePrioritizationTaskStore.getState().loadTasksFromSupabase?.();
  }

  // ── WARM: cycles, tracks, sessions, loops, proposals, relationships ─────────
  if (force || isStale('sessions', WARM_TTL_MS)) {
    markFetched('sessions');
    useSessionStore.getState().fetchFromSupabase?.();
  }

  if (force || isStale('loops', WARM_TTL_MS)) {
    markFetched('loops');
    useCatchballLoopStore.getState().fetchFromSupabase?.();
  }

  if (force || isStale('relationships', WARM_TTL_MS)) {
    markFetched('relationships');
    useItemRelationshipsStore.getState().fetchFromSupabase?.();
  }

  if (force || isStale('tracks', WARM_TTL_MS)) {
    markFetched('tracks');
    void useTrackStore.getState().fetchAllTracks?.();
  }

  if (force || isStale('cycles', WARM_TTL_MS)) {
    markFetched('cycles');
    void useDegapProcessStore.getState().fetchFromSupabase?.();
  }

  // One read for the workspace, however many cycles it holds. Not marked fetched
  // until a workspace is known, so an early call cannot skip the real one.
  const workspaceId = getCurrentWorkspaceId();
  if (workspaceId && (force || isStale('proposals', WARM_TTL_MS))) {
    markFetched('proposals');
    void useProposalPacketStore.getState().fetchProposalsForWorkspace(workspaceId);
  }

  // ── COOL: attributes, item pools / publications ─────────────────────────────
  if (force || isStale('pools', COOL_TTL_MS)) {
    markFetched('pools');
    useItemPoolsStore.getState().fetchFromSupabase?.();
  }

  if (force || isStale('attributes', COOL_TTL_MS)) {
    markFetched('attributes');
    useAttributesStore.getState().fetchFromSupabase?.();
  }
}
